import ResortTemplate, {
  type ResortGalleryItem,
  type ResortStat,
} from "@/components/ResortTemplate";
import { mediaUrl } from "@/config/api";

type ResortLandingPageProps = {
  title: string;
  subtitle: string;
  cityState: string;
  description: string;
  bannerPath: string;
  galleryPaths: string[];
  address: string;
  bookingPhone: string;
  whatsapp: string;
  email: string;
  /** Slug do empreendimento na API (link_interno) para carregar FAQ. */
  developmentSlug?: string;
};

const splitParagraphs = (text: string): string[] => {
  if (!text) return [];
  return text
    .split(/\n+/)
    .map((part) => part.trim())
    .filter(Boolean);
};

const buildGallery = (paths: string[], title: string): ResortGalleryItem[] => {
  if (!paths.length) return [];
  return paths.slice(0, 6).map((src, index) => ({
    src: mediaUrl(src),
    alt: `${title} — galeria ${index + 1}`,
    span: index === 0 ? "big" : index === 3 ? "wide" : undefined,
  }));
};

const ResortLandingPage = ({
  title,
  subtitle,
  cityState,
  description,
  bannerPath,
  galleryPaths,
  address,
  bookingPhone,
  whatsapp,
  email,
  developmentSlug,
}: ResortLandingPageProps) => {
  const paragraphs = splitParagraphs(description);

  const stats: ResortStat[] = [
    { value: "5★", label: "Experiência" },
    { value: "24h", label: "Atendimento" },
    { value: "100%", label: "Conforto" },
  ];

  const aboutImage = mediaUrl(galleryPaths[0] ?? bannerPath);

  return (
    <ResortTemplate
      developmentSlug={developmentSlug}
      name={title}
      location={cityState}
      heroImage={mediaUrl(bannerPath)}
      heroDescription={subtitle}
      about={{
        eyebrow: "Sobre o empreendimento",
        title: `Conheça o ${title}`,
        paragraphs:
          paragraphs.length > 0
            ? paragraphs
            : ["Conforto, sofisticação e atendimento personalizado em uma experiência única."],
        image: aboutImage,
        imageAlt: `${title} — vista do empreendimento`,
        stats,
      }}
      gallery={
        galleryPaths.length > 0
          ? {
              title: "Cada detalhe pensado para encantar",
              description:
                "Explore os ambientes que tornam a sua estadia única — do nascer ao pôr do sol.",
              items: buildGallery(galleryPaths, title),
            }
          : undefined
      }
      location_section={{
        title: `Visite o ${title}`,
        description: `Estamos prontos para receber você em ${cityState}. Reservas: ${bookingPhone} · WhatsApp: ${whatsapp} · ${email}`,
        contact: {
          address,
          cityState,
          phone: bookingPhone,
          travelInfo: `Reservas: ${bookingPhone} · WhatsApp: ${whatsapp}`,
          mapEmbedUrl: `https://www.google.com/maps?q=${encodeURIComponent(`${address}, ${cityState}`)}&output=embed`,
          directionsUrl: `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${address}, ${cityState}`)}`,
        },
      }}
    />
  );
};

export default ResortLandingPage;
